// src/pages/packages/components/PackageCard.jsx
import React, { useMemo } from "react";
import {
  faBan,
  faBoxOpen,
  faCalendarAlt,
  faCheckCircle,
  faClipboardList,
  faMoneyBillWave,
  faPen,
  faToggleOff,
  faToggleOn,
  faTrash,
  faUserPlus,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

import ActionPillButton from "../../../../commons/buttons/ActionPillButton";
import { Badge } from "react-bootstrap";
import CustomCard from "../../../../commons/cards/Card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

function formatBRL(v) {
  const n = Number(v || 0);
  try {
    return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  } catch {
    return `R$ ${n.toFixed(2)}`;
  }
}

function toBrDate(iso) {
  if (!iso) return null;
  const s = String(iso).slice(0, 10); // yyyy-mm-dd
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return null;
  return `${d}/${m}/${y}`;
}

function statusVariant(status) {
  return status === "ACTIVE" ? "success" : "secondary";
}

function statusLabel(status) {
  return status === "ACTIVE" ? "Ativo" : "Inativo";
}



export default function PackageCard({ pkg, onEdit, onToggleStatus, onDelete, onView, onAddCustomers,onViewCustomers }) {
  const active = pkg?.status === "ACTIVE";

  const priceLabel = useMemo(() => formatBRL(pkg?.priceAmount), [pkg?.priceAmount]);

  const expiryLabel = useMemo(() => {
    const br = toBrDate(pkg?.expiryDate);
    return br ? `Válido até ${br}` : "Sem validade";
  }, [pkg?.expiryDate]);

  const itemsCount = useMemo(() => {
    const n =
      pkg?.itemsCount != null
        ? Number(pkg.itemsCount)
        : Array.isArray(pkg?.items)
          ? pkg.items.length
          : 0;
    return Number.isFinite(n) ? n : 0;
  }, [pkg?.itemsCount, pkg?.items]);

  const totalSessions = useMemo(() => {
    const n =
      pkg?.totalSessions != null
        ? Number(pkg.totalSessions)
        : Array.isArray(pkg?.items)
          ? pkg.items.reduce((acc, it) => acc + Number(it?.quantity || 0), 0)
          : 0;
    return Number.isFinite(n) ? n : 0;
  }, [pkg?.totalSessions, pkg?.items]);

  const subtitle = useMemo(() => {
    // Ex: "R$ 299,00 • 3 serviços • 10 sessões"
    const parts = [priceLabel];

    if (itemsCount) parts.push(`${itemsCount} serviço(s)`);
    if (totalSessions) parts.push(`${totalSessions} sessão(ões)`);

    return parts.join(" • ");
  }, [priceLabel, itemsCount, totalSessions]);

  return (
    <div style={{ width: "100%" }}>
      <CustomCard>
        {/* Header */}
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div style={{ minWidth: 0 }}>
            <div className="fw-semibold" style={{ fontSize: 16, lineHeight: "20px" }}>
              {pkg?.name || "Pacote"}
            </div>

            <div className="text-muted" style={{ fontSize: 12 }}>
              {pkg?.description || subtitle}
            </div>
          </div>

          <div className="d-flex flex-column align-items-end gap-2">
            <Badge bg={statusVariant(pkg?.status)} pill>
              {statusLabel(pkg?.status)}
            </Badge>

            <ActionPillButton
              icon={active ? faToggleOff : faToggleOn}
              label={active ? "Inativar" : "Ativar"}
              onClick={onToggleStatus}
              color={active ? "#b42318" : "#198754"}
              borderColor={active ? "#ffd6d6" : "#c9f2d8"}
              title={active ? "Inativar pacote" : "Ativar pacote"}
            />
          </div>
        </div>

        <hr className="my-3" />

        {/* Badges */}
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Badge bg="light" text="dark" pill style={{ fontWeight: 900 }}>
            <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
            {priceLabel}
          </Badge>

          <Badge bg="light" text="dark" pill style={{ fontWeight: 900 }}>
            <FontAwesomeIcon icon={faClipboardList} className="me-1" />
            {itemsCount || "0"} serviço(s)
          </Badge>

          <Badge bg="light" text="dark" pill style={{ fontWeight: 900 }}>
            <FontAwesomeIcon icon={faBoxOpen} className="me-1" />
            {totalSessions || "0"} sessão(ões)
          </Badge>

          <Badge bg="light" text="dark" pill style={{ fontWeight: 900 }}>
            <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
            {expiryLabel}
          </Badge>

          <Badge
            bg={active ? "success" : "secondary"}
            pill
            style={{ fontWeight: 900 }}
            title={active ? "Pode vender normalmente" : "Não aparece para venda"}
          >
            <FontAwesomeIcon icon={active ? faCheckCircle : faBan} className="me-1" />
            {active ? "Disponível para venda" : "Indisponível"}
          </Badge>
        </div>

        {/* Infos principais (linhas) */}
        <div className="d-flex flex-column gap-2">
          <div className="d-flex align-items-center justify-content-between">
            <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
              <span style={{ fontWeight: 900 }}>∑</span>
              <span>Total de sessões</span>
            </div>
            <div className="fw-semibold" style={{ fontSize: 13 }}>
              {totalSessions}
            </div>
          </div>
        </div>

        {/* Ações */}
        <div className="d-flex gap-2 flex-wrap mt-3">
          {onView ? (
            <ActionPillButton
              icon={faClipboardList}
              label="Detalhes"
              onClick={onView}
              color="#0891b2"
              borderColor="#cffafe"
              title="Ver detalhes do pacote"
            />
          ) : null}

          <ActionPillButton
            icon={faPen}
            label="Editar"
            onClick={onEdit}
            color="#2f2a7a"
            borderColor="#dfe3ff"
            title="Editar pacote"
          />

          <ActionPillButton
            icon={faTrash}
            label="Excluir"
            onClick={onDelete}
            color="#b42318"
            borderColor="#ffd6d6"
            title="Excluir pacote"
          />

          <ActionPillButton
            icon={faUserPlus}
            label="Adicionar Cliente"
            onClick={onAddCustomers}
            color="#059669"
            borderColor="#d1fae5"
            title="Adicionar clientes ao pacote"
          />

          <ActionPillButton
            icon={faUsers}
            label="Ver Clientes"
            onClick={onViewCustomers}
            color="#0891b2"
            borderColor="#cffafe"
            title="Ver clientes do clube"
          />
        </div>

      </CustomCard>
    </div>
  );
}